import axios from 'axios';
import { downloadMediaUrl } from './zipDownload';
import { buildDownloaderFilename, getImageExtension, toOriginalImageUrl } from './mediaUtils';

// Define supported platform types
export type Platform = 'x';

// Define quality options
export type VideoQuality = 'high' | 'medium' | 'low';

// Define tweet info interface
interface TweetInfo {
  username: string;
  id: string;
}

// Define FXTwitter API response types
interface FXTwitterVideoVariant {
  url: string;
  bitrate?: number;
}

interface FXTwitterVideo {
  url?: string;
  thumbnail_url?: string;
  preview_image_url?: string;
  duration?: number;
  width?: number;
  height?: number;
  variants?: FXTwitterVideoVariant[];
}

interface FXTwitterPhoto {
  url: string;
  width?: number;
  height?: number;
}

interface FXTwitterMedia {
  videos?: FXTwitterVideo[];
  photos?: FXTwitterPhoto[];
}

interface FXTwitterAuthor {
  name?: string;
}

interface FXTwitterTweet {
  text: string;
  media?: FXTwitterMedia;
  author?: FXTwitterAuthor;
}

interface FXTwitterResponse {
  code: number;
  message: string;
  tweet: FXTwitterTweet;
}

// Video metadata interface
export interface VideoMetadata {
  title: string;
  thumbnail: string;
  author?: string;
  platform: Platform;
  availableQualities: VideoQuality[];
  tweetId?: string;
  url: string;
  videos: Array<{
    url: string;
    thumbnail: string;
    duration?: number;
    width?: number;
    height?: number;
    variants?: Array<{
      url: string;
      bitrate?: number;
    }>;
    playerUrl: string;
  }>;
  images?: Array<{
    url: string;
    width?: number;
    height?: number;
  }>;
  selectedVideoIndex: number;
}

// Function to extract tweet ID from X/Twitter URL
const getXTweetId = (url: string): TweetInfo | null => {
  console.log('Processing URL:', url);
  // Handle both old twitter.com and new x.com URLs
  const regExp = /(?:twitter\.com|x\.com)\/(\w+)\/status\/(\d+)/;
  const match = url.match(regExp);
  console.log('URL match result:', match);
  return match ? { username: match[1], id: match[2] } : null;
};

// Function to get video info from X/Twitter
const getXVideoInfo = async (url: string): Promise<VideoMetadata> => {
  const tweetInfo = getXTweetId(url);
  if (!tweetInfo) throw new Error('Invalid X/Twitter URL');

  try {
    // Use fxtwitter API which allows CORS
    const response = await axios.get<FXTwitterResponse>(
      `https://api.fxtwitter.com/${tweetInfo.username}/status/${tweetInfo.id}`
    );

    if (!response.data?.tweet) {
      throw new Error('Failed to fetch tweet data');
    }

    const tweet = response.data.tweet;

    // Check for media content
    if (!tweet.media) {
      throw new Error('No media found in this X/Twitter post');
    }

    // Get all videos and photos from the tweet
    const videos = tweet.media.videos || [];
    const photos = tweet.media.photos || [];

    if (videos.length === 0 && photos.length === 0) {
      throw new Error('No videos or images found in this X/Twitter post');
    }

    // Process all videos
    const processedVideos = videos.map((video: FXTwitterVideo) => ({
      url: video.url || '',
      thumbnail: video.thumbnail_url || video.preview_image_url || '',
      duration: video.duration,
      width: video.width,
      height: video.height,
      variants: video.variants || [{ url: video.url || '' }],
      // Add a mobile-friendly player URL
      playerUrl: `https://fxtwitter.com/${tweetInfo.username}/status/${tweetInfo.id}/video/${videos.indexOf(video)}`
    }));

    // Process all photos
    const processedPhotos = photos.map((photo: FXTwitterPhoto) => ({
      url: photo.url || '',
      width: photo.width,
      height: photo.height
    }));

    return {
      title: tweet.text || 'X Media',
      thumbnail: processedVideos[0]?.thumbnail || photos[0]?.url || '',
      author: tweet.author?.name || tweetInfo.username,
      platform: 'x',
      availableQualities: ['high', 'medium', 'low'],
      tweetId: tweetInfo.id,
      url: url,
      videos: processedVideos,
      images: processedPhotos,
      selectedVideoIndex: 0
    };
  } catch (error) {
    console.error('Error fetching tweet:', error);
    throw error;
  }
};

// Function to get video info from supported platforms
const getVideoInfo = async (url: string): Promise<VideoMetadata> => {
  if (url.includes('twitter.com') || url.includes('x.com')) {
    return getXVideoInfo(url);
  }
  
  throw new Error('Only X/Twitter videos are supported');
};

const downloadVideo = async (
  url: string,
  quality: VideoQuality,
  downloadPath: string,
  selectedVideoIndex: number = 0,
  customFilename: string = ''
): Promise<{ success: boolean; filePath: string }> => {
  try {
    const tweetInfo = getXTweetId(url);
    if (!tweetInfo) {
      throw new Error('Invalid X/Twitter URL');
    }

    const mediaInfo = await getXVideoInfo(url);
    if (!mediaInfo) {
      throw new Error('No media found in this X/Twitter post');
    }

    // Determine if we're downloading a video or image based on the selectedVideoIndex
    const isVideo = selectedVideoIndex < mediaInfo.videos.length;
    let downloadUrl: string;
    let mimeType: string;
    let extension: string;

    if (isVideo) {
      // Handle video download
      const video = mediaInfo.videos[selectedVideoIndex];
      if (!video) {
        throw new Error('Selected video not found');
      }

      const variants = video.variants || [{ url: video.url }];
      // Sort variants by quality (bitrate or resolution)
      const mp4Variants = variants
        .filter(v => v.url && v.url.includes('.mp4'))
        .sort((a, b) => {
          // If we have bitrates, use them
          if (a.bitrate && b.bitrate) {
            return (b.bitrate || 0) - (a.bitrate || 0);
          }
          // Otherwise try to extract quality from URL
          const getQuality = (url: string) => {
            const match = url.match(/\/(\d+)x(\d+)\//);
            return match ? parseInt(match[2]) : 0;
          };
          return getQuality(b.url || '') - getQuality(a.url || '');
        });

      if (mp4Variants.length === 0) {
        throw new Error('No MP4 video URL found');
      }

      // Select variant based on quality preference
      let selectedVariant;
      switch (quality) {
        case 'high':
          selectedVariant = mp4Variants[0]; // First is highest quality
          break;
        case 'medium':
          selectedVariant = mp4Variants[Math.floor(mp4Variants.length / 2)];
          break;
        case 'low':
          selectedVariant = mp4Variants[mp4Variants.length - 1];
          break;
        default:
          selectedVariant = mp4Variants[0];
      }

      downloadUrl = selectedVariant.url;
      mimeType = 'video/mp4';
      extension = 'mp4';
    } else {
      // Handle image download
      const imageIndex = selectedVideoIndex - mediaInfo.videos.length;
      if (!mediaInfo.images || !mediaInfo.images[imageIndex]) {
        throw new Error('Selected image not found');
      }
      downloadUrl = toOriginalImageUrl(mediaInfo.images[imageIndex].url);
      mimeType = 'image/jpeg';
      extension = 'jpg';
    }

    if (downloadUrl.startsWith('http:')) {
      downloadUrl = downloadUrl.replace('http:', 'https:');
    }

    if (!isVideo) {
      extension = getImageExtension(downloadUrl);
      mimeType =
        extension === 'png'
          ? 'image/png'
          : extension === 'webp'
            ? 'image/webp'
            : extension === 'gif'
              ? 'image/gif'
              : 'image/jpeg';
    }

    const totalMediaCount = (mediaInfo.videos || []).length + (mediaInfo.images || []).length;
    let mediaSuffix = '';
    if (totalMediaCount > 1) {
      mediaSuffix = isVideo
        ? `_vid_${selectedVideoIndex + 1}`
        : `_img_${selectedVideoIndex - mediaInfo.videos.length + 1}`;
    }

    const filename = buildDownloaderFilename({
      customFilename,
      username: tweetInfo.username,
      title: mediaInfo.title,
      extension,
      mediaSuffix,
    });

    await downloadMediaUrl(downloadUrl, filename, mimeType);
    return { success: true, filePath: filename };
  } catch (error) {
    console.error('Error downloading media:', error);
    throw error;
  }
};

export const videoService = {
  getVideoInfo,
  downloadVideo
}; 